import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUser } from '@/context/user-provider';
import { toast } from 'sonner';

export const DeviceAuthorizationPage = () => {
  const [searchParams] = useSearchParams();
  const initialUserCode = searchParams.get('user_code') || '';
  const [userCode, setUserCode] = useState(initialUserCode);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();
  const { user, isLoading } = useUser();

  // Check if user is already authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      // Redirect to login with return URL to this page
      const returnTo = window.location.pathname + window.location.search;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, [user, isLoading, navigate]);

  // If there's a user_code in the URL, redirect directly to approval page
  useEffect(() => {
    if (initialUserCode && !isLoading && user) {
      // Redirect directly to approval page with the user code
      navigate(`/device/approve?user_code=${initialUserCode}`);
    }
  }, [initialUserCode, isLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsVerifying(true);

    try {
      // Format the code: remove dashes and convert to uppercase
      const formattedCode = userCode.trim().replace(/-/g, '').toUpperCase();

      if (formattedCode.length !== 8) {
        setError('Code must be 8 characters');
        setIsVerifying(false);
        return;
      }

      // Check if the code is valid using GET /device endpoint
      const response = await authClient.device({
        query: { user_code: formattedCode },
      });

      if (response.data) {
        // Redirect to approval page with the user code as a parameter
        // Clear search params to avoid auto-submit on approval page
        navigate(`/device/approve?user_code=${formattedCode}`);
      } else {
        setError('Invalid or expired code');
      }
    } catch (err: any) {
      console.error('Device code verification error:', err);
      setError(err?.message || 'Invalid or expired code');
    } finally {
      setIsVerifying(false);
    }
  };

  // If user is still loading, show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // If user is not authenticated, don't render anything (they'll be redirected by useEffect)
  if (!user) {
    return null;
  }



  // If there's an initial user code, just show a loading state while we redirect
  if (initialUserCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-xl">Device Authorization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p>Verifying device code...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show device code entry form for manual entry
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl">Device Authorization</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="userCode" className="text-sm font-medium text-muted-foreground">
                Enter device code
              </label>
              <Input
                id="userCode"
                type="text"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                placeholder="ABCD-1234"
                maxLength={8}
                className="font-mono text-center text-xl tracking-widest uppercase"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Enter the code displayed on your device
              </p>
            </div>
            
            {error && (
              <div className="text-sm text-destructive">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isVerifying || userCode.length !== 8}
            >
              {isVerifying ? 'Verifying...' : 'Continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};